// DataTable.tsx
import React from "react";
import { patientFields } from "../../types/patient";

interface DataTableProps {
  data: any[];
  loading: boolean;
  onRowClick?: (item: any) => void;
}

const DataTable: React.FC<DataTableProps> = ({ data, loading, onRowClick }) => {
  if (loading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm text-center">
        <p className="text-gray-500 text-sm">Φόρτωση δεδομένων...</p>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm text-center">
        <p className="text-gray-500 text-sm">Δεν βρέθηκαν ασθενείς.</p>
      </div>
    );
  }

  // Βοηθητική συνάρτηση για τη διαμόρφωση των τιμών (Select Labels, Dates κλπ)
  const renderCellValue = (item: any, field: typeof patientFields[number]) => {
    const rawValue = item[field.name];

    // 1. Αν η τιμή είναι κενή
    if (rawValue === null || rawValue === undefined || rawValue === "") {
      return "-";
    }

    // 2. Αν είναι πεδίο επιλογής (π.χ. Φύλο / Select)
    if (field.options) {
      const option = field.options.find(
        (opt) => String(opt.value) === String(rawValue)
      );
      return option ? option.label : rawValue;
    }

    // 3. Αν είναι πεδίο Ημερομηνίας (date)
    if (field.type === "date") {
      const date = new Date(rawValue);
      
      // Έλεγχος αν η ημερομηνία είναι έγκυρη
      if (!isNaN(date.getTime())) {
        return new Intl.DateTimeFormat("el-GR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        }).format(date); // Παράγει μορφή: DD/MM/YYYY (π.χ. 15/04/1990)
      }
    }

    return rawValue;
  };

  return (
    <div className="overflow-x-auto bg-white rounded-lg shadow-sm border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {patientFields.map((field) => (
              <th
                key={field.name}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                {field.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.map((item, index) => (
            <tr
              key={item.id || item.patientId || index}
              onClick={() => onRowClick && onRowClick(item)}
              className="hover:bg-gray-50 cursor-pointer transition-colors"
            >
              {patientFields.map((field) => (
                <td
                  key={field.name}
                  className="px-6 py-4 whitespace-nowrap text-sm text-gray-700"
                >
                  {renderCellValue(item, field)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default DataTable;